import React, { useState, useEffect } from "react";
import axios from "axios";
import { Typeahead } from "react-bootstrap-typeahead";
import { navigate } from "@reach/router";
import { AuthConsumer } from "../helpers/AuthContext";
import GoogleSignInButton from "../components/GoogleSignInButton";
import {
  EDU_OPTIONS,
  YEAR_OPTIONS,
  CLEAN_OPTIONS,
} from "../constants/profileOptions";

// Fuzzy match for the lab suggestion box: ignore case, spaces, hyphens, and
// punctuation so typing "ferris lab" surfaces an existing "Ferris-Lab" as a
// suggestion to pick. This ONLY affects what's suggested — it never merges
// anything automatically; the admin merge tool is the deliberate way to
// combine differently-named labs.
const normLab = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const labFilterBy = (option, props) => {
  const opt = typeof option === "string" ? option : (option && option.lab) || "";
  const typed = normLab(props.text);
  if (!typed) return true;
  return normLab(opt).includes(typed);
};

const SignUp = () => {
  const [fname, setFname] = useState("");
  const [lname, setLname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmpass, setConfirmpass] = useState("");
  const [assoc, setAssoc] = useState([]);
  const [typedAssoc, setTypedAssoc] = useState("");
  const [lab, setLab] = useState([]);
  const [typedLab, setTypedLab] = useState("");
  const [labOptions, setLabOptions] = useState([]);
  const [edu, setEdu] = useState("");
  const [exp, setExp] = useState("");
  const [expEEG, setExpEEG] = useState("");
  const [expMoBI, setExpMoBI] = useState("");
  const [expICA, setExpICA] = useState("");
  const [cleanFreq, setCleanFreq] = useState("");

  const [options, setOptions] = useState([]);
  const [message, setMessage] = useState("");
  const [success, setStatus] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch known associations once
  useEffect(() => {
    axios
      .get("/api/users/userlist")
      .then((res) => {
        const unique = [
          ...new Set(
            res.data.map((u) => u.assoc).filter((a) => a !== undefined)
          ),
        ];
        setOptions(unique);
      })
      .catch(() => {
        // Non-fatal — typeahead falls back to free text
      });

    // Existing lab names so people pick the SAME lab instead of retyping a
    // new spelling. Public endpoint (works even though signup isn't logged in).
    axios
      .get("/api/users/labs")
      .then((res) => setLabOptions((res.data && res.data.labs) || []))
      .catch(() => {
        // Non-fatal — falls back to free text (add-new still works)
      });
  }, []);

  const validate = () => {
    if (!fname || !lname) return "Enter your first and last name.";
    if (!assoc[0] && !typedAssoc) return "Select or enter your university / company.";
    if (!edu) return "Select your highest degree.";
    if (!exp || !expEEG || !expMoBI || !expICA) return "Answer all expertise questions.";
    if (!cleanFreq) return "Tell us how often you clean data.";
    if (!email || !password) return "Enter your email and a password.";
    const emailLower = email.toLowerCase();
    const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/;
    if (!emailRegex.test(emailLower)) return "Enter a valid email address.";
    if (password.length < 6) return "Password must be at least 6 characters.";
    if (password !== confirmpass) return "Passwords do not match.";
    return null;
  };

  const resolveAssoc = () => {
    if (typeof assoc[0] === "string" || assoc[0] instanceof String) return assoc[0];
    if (typeof assoc[0] === "object" && assoc[0] !== null) return assoc[0].assoc;
    return typedAssoc;
  };

  // Lab is optional. A picked existing lab is a string; an "add new" entry is
  // an object carrying the typed value; otherwise fall back to raw typed text.
  const resolveLab = () => {
    if (typeof lab[0] === "string" || lab[0] instanceof String) return lab[0];
    if (typeof lab[0] === "object" && lab[0] !== null) return lab[0].lab || "";
    return typedLab;
  };

  const handleSubmit = (signup) => {
    const err = validate();
    if (err) {
      setStatus(false);
      setMessage(err);
      return;
    }

    const emailLower = email.toLowerCase();
    const assocSel = resolveAssoc();

    setIsSubmitting(true);
    setMessage("");

    signup({
      fname,
      lname,
      assocSel,
      lab: resolveLab().trim(),
      edu,
      exp,
      expEEG,
      expMoBI,
      expICA,
      cleanFreq,
      emailLower,
      password,
    })
      .then(() => {
        // Account created but NOT active yet — the server emailed a 6-digit
        // code. Send them to the verify page (no auto-login; sign-in is
        // gated until the email is verified).
        setIsSubmitting(false);
        setStatus(true);
        navigate("/verify-email?email=" + encodeURIComponent(emailLower));
      })
      .catch((reason) => {
        setIsSubmitting(false);
        setStatus(false);
        const errorMessage =
          reason?.response?.data?.message ||
          "Signup failed. Please check your details and try again.";
        setMessage(errorMessage);
      });
  };

  // Sign up / in with Google. A brand-new Google account has no profile
  // yet, so it's routed to the profile form; a returning one goes straight
  // to the dashboard.
  const handleGoogle = (googleLogin, credential) => {
    setStatus(false);
    setMessage("");
    setIsSubmitting(true);
    googleLogin(credential)
      .then((data) => {
        setIsSubmitting(false);
        navigate(data && data.profileComplete === false ? "/complete-profile" : "/dashboard");
      })
      .catch((err) => {
        setIsSubmitting(false);
        setStatus(false);
        setMessage(
          err?.response?.data?.message || "Google sign-in failed. Please try again."
        );
      });
  };

  return (
    <AuthConsumer>
      {({ Auth, signup, googleLogin }) => {
        const isLoggedIn = Auth === "Admin" || Auth === "User";

        if (isLoggedIn) {
          return (
            <div className="theme-scientific auth-page">
              <div className="auth-shell auth-shell--solo-wide">
                <div className="auth-form-panel">
                  <div className="sci-eyebrow">ICMOBI / Session active</div>
                  <h1 className="auth-form-panel__title">Already signed in</h1>
                  <p className="auth-form-panel__subtitle">
                    Sign out first if you'd like to register a new account.
                  </p>
                  <button
                    className="sci-btn sci-btn--primary sci-btn--full"
                    style={{ marginTop: 24 }}
                    onClick={() => navigate("/dashboard")}
                  >
                    Go to Dashboard →
                  </button>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="theme-scientific auth-page">
            <div className="auth-shell auth-shell--solo-wide">
              <section className="auth-form-panel">
                <header className="auth-form-panel__header">
                  <div className="sci-eyebrow">ICMOBI / Sign up</div>
                  <h2 className="auth-form-panel__title">Create your account</h2>
                  <p className="auth-form-panel__subtitle">
                    A quick profile helps us weight your labels.
                  </p>
                </header>

                <GoogleSignInButton
                  text="signup_with"
                  onCredential={(cred) => handleGoogle(googleLogin, cred)}
                  onError={() => {
                    setStatus(false);
                    setMessage("Couldn't start Google sign-in. Please try again.");
                  }}
                />
                <div className="auth-divider">or sign up with email</div>

                {message && (
                  <div
                    className={`sci-alert ${
                      success ? "sci-alert--success" : "sci-alert--danger"
                    }`}
                  >
                    <div className="sci-alert__icon" />
                    <span>{message}</span>
                  </div>
                )}

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(signup);
                  }}
                >
                  {/* Identity */}
                  <div className="sci-section" style={{ marginTop: 0, paddingTop: 0, borderTop: 0 }}>
                    <div className="sci-section__title">Identity</div>
                    <div className="sci-field-row sci-field-row--2">
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req" htmlFor="su-fname">
                          First name
                        </label>
                        <input
                          id="su-fname"
                          type="text"
                          className="sci-input"
                          placeholder="Ada"
                          value={fname}
                          onChange={(e) => setFname(e.target.value)}
                          autoComplete="given-name"
                        />
                      </div>
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req" htmlFor="su-lname">
                          Last name
                        </label>
                        <input
                          id="su-lname"
                          type="text"
                          className="sci-input"
                          placeholder="Lovelace"
                          value={lname}
                          onChange={(e) => setLname(e.target.value)}
                          autoComplete="family-name"
                        />
                      </div>
                    </div>
                    <div className="sci-field">
                      <label className="sci-field__label sci-field__label--req">
                        University / company
                      </label>
                      <Typeahead
                        id="assoc-typeahead-single"
                        labelKey="assoc"
                        allowNew
                        onChange={setAssoc}
                        onInputChange={setTypedAssoc}
                        options={options}
                        placeholder="Start typing an institution…"
                      />
                    </div>
                    <div className="sci-field">
                      <label className="sci-field__label">
                        Research lab / group <span style={{ fontWeight: 400, color: "#6b7280" }}>(optional)</span>
                      </label>
                      <Typeahead
                        id="su-lab-typeahead"
                        labelKey="lab"
                        allowNew
                        newSelectionPrefix="Add new lab: "
                        filterBy={labFilterBy}
                        onChange={setLab}
                        onInputChange={setTypedLab}
                        options={labOptions}
                        placeholder="Pick your lab, or type to add a new one…"
                      />
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                        Please pick your lab from the list if it's already there —
                        it keeps your lab's competition score together.
                      </div>
                    </div>
                  </div>

                  {/* Expertise */}
                  <div className="sci-section">
                    <div className="sci-section__title">Expertise ratings</div>
                    <div className="sci-field-row sci-field-row--2">
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req">
                          Highest degree
                        </label>
                        <select
                          className="sci-select"
                          value={edu}
                          onChange={(e) => setEdu(e.target.value)}
                        >
                          {EDU_OPTIONS.map((o) => (
                            <option key={o.value || "placeholder"} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req">
                          Years labeling EEG
                        </label>
                        <select
                          className="sci-select"
                          value={exp}
                          onChange={(e) => setExp(e.target.value)}
                        >
                          {YEAR_OPTIONS.map((o) => (
                            <option key={"labeling-" + (o.value || "p")} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="sci-field-row sci-field-row--3">
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req">
                          Years with EEG
                        </label>
                        <select
                          className="sci-select"
                          value={expEEG}
                          onChange={(e) => setExpEEG(e.target.value)}
                        >
                          {YEAR_OPTIONS.map((o) => (
                            <option key={"eeg-" + (o.value || "p")} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req">
                          Years with MoBI
                        </label>
                        <select
                          className="sci-select"
                          value={expMoBI}
                          onChange={(e) => setExpMoBI(e.target.value)}
                        >
                          {YEAR_OPTIONS.map((o) => (
                            <option key={"mobi-" + (o.value || "p")} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req">
                          Years with ICA
                        </label>
                        <select
                          className="sci-select"
                          value={expICA}
                          onChange={(e) => setExpICA(e.target.value)}
                        >
                          {YEAR_OPTIONS.map((o) => (
                            <option key={"ica-" + (o.value || "p")} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="sci-field">
                      <label className="sci-field__label sci-field__label--req">
                        How often do you clean data yourself?
                      </label>
                      <select
                        className="sci-select"
                        value={cleanFreq}
                        onChange={(e) => setCleanFreq(e.target.value)}
                      >
                        {CLEAN_OPTIONS.map((o) => (
                          <option key={"clean-" + (o.value || "p")} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Credentials */}
                  <div className="sci-section">
                    <div className="sci-section__title">Credentials</div>
                    <div className="sci-field">
                      <label className="sci-field__label sci-field__label--req" htmlFor="su-email">
                        Email
                      </label>
                      <input
                        id="su-email"
                        type="email"
                        className="sci-input"
                        placeholder="you@institution.edu"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                      />
                    </div>
                    <div className="sci-field-row sci-field-row--2">
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req" htmlFor="su-pass">
                          Password
                        </label>
                        <input
                          id="su-pass"
                          type="password"
                          className="sci-input"
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="new-password"
                        />
                      </div>
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req" htmlFor="su-pass2">
                          Confirm
                        </label>
                        <input
                          id="su-pass2"
                          type="password"
                          className="sci-input"
                          placeholder="••••••••"
                          value={confirmpass}
                          onChange={(e) => setConfirmpass(e.target.value)}
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="sci-btn sci-btn--primary sci-btn--full"
                    disabled={isSubmitting}
                    style={{ marginTop: 24 }}
                  >
                    {isSubmitting ? "Creating account…" : "Create account →"}
                  </button>
                </form>

                <div className="auth-footer-link">
                  <span>Already a labeler?</span>
                  <a
                    href="/login"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/login");
                    }}
                  >
                    Sign in →
                  </a>
                </div>
              </section>
            </div>
          </div>
        );
      }}
    </AuthConsumer>
  );
};

export default SignUp;
